import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("cf_user");
    return u ? JSON.parse(u) : null;
  });
  const [permissions, setPermissions] = useState([]);
  const [scopeType, setScopeType] = useState("all");
  const [workspaceKey, setWorkspaceKey] = useState(null);
  const [permsLoaded, setPermsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/permissions/me");
      setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
      setScopeType(data.scope_type || "all");
      setWorkspaceKey(data.workspace_key || null);
    } catch {
      setPermissions([]);
    } finally {
      setPermsLoaded(true);
    }
  }, []);

  const applyToken = useCallback(async (token, fallbackUser = null) => {
    localStorage.setItem("cf_token", token);
    await loadPermissions();
    // Fetch the FULL profile (kitchen_type, operational_scope, …) so the
    // permission+scope sidebar and role-based routing resolve correctly.
    let profile = fallbackUser;
    try {
      const me = await api.get("/auth/me");
      profile = me.data;
    } catch { /* fall back to provided payload */ }
    if (profile) {
      localStorage.setItem("cf_user", JSON.stringify(profile));
      setUser(profile);
    }
    return profile;
  }, [loadPermissions]);

  const login = useCallback(async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    return applyToken(data.access_token, data.user);
  }, [applyToken]);

  const logout = useCallback(() => {
    localStorage.removeItem("cf_token");
    localStorage.removeItem("cf_user");
    setUser(null);
    setPermissions([]);
    window.location.href = "/login";
  }, []);

  // refresh profile + permissions on mount if token exists (run once). Always
  // re-fetch the full profile so stale localStorage users gain kitchen_type/scope.
  useEffect(() => {
    const token = localStorage.getItem("cf_token");
    if (token) {
      api.get("/auth/me").then(({ data }) => {
        setUser(data);
        localStorage.setItem("cf_user", JSON.stringify(data));
      }).catch(() => {});
      loadPermissions();
    }
    // eslint-disable-next-line
  }, []);

  const isRole = useCallback((...roles) => user && roles.includes(user.role), [user]);
  const hasPermission = useCallback((perm) => !perm || permissions.includes(perm), [permissions]);
  const hasAnyPermission = useCallback(
    (perms = []) => perms.length === 0 || perms.some((p) => permissions.includes(p)),
    [permissions]
  );

  const value = useMemo(
    () => ({ user, login, logout, loading, isRole, permissions, scopeType, workspaceKey, permsLoaded, hasPermission, hasAnyPermission, refreshPermissions: loadPermissions, applyToken }),
    [user, login, logout, loading, isRole, permissions, scopeType, workspaceKey, permsLoaded, hasPermission, hasAnyPermission, loadPermissions, applyToken]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
